import React, { useState, useRef, useEffect, useCallback } from "react";
import { Controller, Control, UseFormSetValue } from "react-hook-form";
import { get } from "react-hook-form";
import { t } from "i18next";
import { REQUIRED_VALIDATOR } from "../../../../messages/errorsMessages";
import FlagImage from "../FlagImage/FlagImage";
import "./EANipInput.scss";

const NIP_POLISH_PATTERN = /^[0-9]{10}$/;
const MAX_FOREIGN_NIP_LENGTH = 14;

interface IProps {
  control: Control<any>;
  name: string;
  countryCodeFieldName: string;
  setValue: UseFormSetValue<any>;
  errors: any;
  label: string;
  countries: string[];
  required?: boolean;
  fullWidth?: boolean;
  showCountrySelect?: boolean;
}

const EANipInput: React.FC<IProps> = ({
  control,
  name,
  countryCodeFieldName,
  setValue,
  errors,
  label,
  countries,
  required = true,
  fullWidth = false,
  showCountrySelect = true,
}) => {
  const [selectedCountry, setSelectedCountry] = useState<string>("PL");
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const listRef = useRef<HTMLUListElement>(null);
  // Track last key pressed for keyboard jump
  const keyBufferRef = useRef<{ key: string; matchIndex: number }>({ key: "", matchIndex: 0 });
  // Always-current ref for selectedCountry — used inside validate closure
  const selectedCountryRef = useRef<string>(selectedCountry);
  selectedCountryRef.current = selectedCountry;

  const handleCountrySelect = (code: string) => {
    setSelectedCountry(code);
    setIsOpen(false);
    setValue(countryCodeFieldName, code === "PL" ? "" : code, { shouldValidate: false });
  };

  const handleToggleDropdown = () => {
    setIsOpen((prev) => !prev);
  };

  // Keyboard navigation: typing a letter jumps to first/next matching country
  const handleDropdownKeyDown = useCallback(
    (e: KeyboardEvent) => {
      if (!isOpen || !listRef.current) return;

      const key = e.key.toUpperCase();
      if (key.length !== 1 || !/[A-Z]/.test(key)) return;

      const matches = countries.filter((code) => code.startsWith(key));
      if (matches.length === 0) return;

      const buf = keyBufferRef.current;
      if (buf.key !== key) {
        buf.key = key;
        buf.matchIndex = 0;
      } else {
        buf.matchIndex = (buf.matchIndex + 1) % matches.length;
      }

      const target = matches[buf.matchIndex];
      const li = listRef.current.querySelector<HTMLLIElement>(`[data-code="${target}"]`);
      if (li) {
        li.scrollIntoView({ block: "nearest" });
      }
    },
    [isOpen, countries]
  );

  useEffect(() => {
    document.addEventListener("keydown", handleDropdownKeyDown);
    return () => document.removeEventListener("keydown", handleDropdownKeyDown);
  }, [handleDropdownKeyDown]);

  // Close on outside click
  const handleClickOutside = useCallback((e: MouseEvent) => {
    if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
      setIsOpen(false);
    }
  }, []);

  useEffect(() => {
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [handleClickOutside]);

  // validate reads from ref — always current country at validation time,
  // even though Controller registers this function only once on mount
  const validationRules = {
    required: required ? REQUIRED_VALIDATOR(t) : false,
    validate: (value: string) => {
      const raw = (value || "").replace(/[^0-9A-Za-z]/g, "");

      // Polska - twarda walidacja 10 cyfr (i komunikat o 10 cyfrach)
      if (!showCountrySelect || selectedCountryRef.current === "PL") {
        if (!NIP_POLISH_PATTERN.test(value || "")) {
          return t("bledny_format_wymagane");
        }
        return true;
      }

      // Inne kraje - brak błędu formatu (przycinamy w onChange),
      // tu tylko fallback gdyby raw był absurdalnie długi (np. z automatycznego wklejenia)
      if (raw.length > MAX_FOREIGN_NIP_LENGTH) {
        return t("bledny_format_wymagane");
      }

      return true;
    },
  };

  const fieldError = get(errors, name);

  return (
    <div className={`ea-nip-input${fullWidth ? " full-width" : ""}`}>
      <label className="ea-nip-input__label">{label}</label>
      <div className={`ea-nip-input__wrapper${fieldError ? " error" : ""}`}>
        {showCountrySelect && (
          <div className="ea-nip-input__country-trigger-wrapper" ref={dropdownRef}>
            <button
              type="button"
              className="ea-nip-input__country-trigger"
              onClick={handleToggleDropdown}
              aria-label="Wybierz kraj"
              aria-expanded={isOpen}
            >
              <FlagImage code={selectedCountry} />
              <span className="ea-nip-input__country-code">{selectedCountry}</span>
              <span className="ea-nip-input__chevron">{isOpen ? "▲" : "▼"}</span>
            </button>

            {isOpen && (
              <ul
                ref={listRef}
                className="ea-nip-input__dropdown-list"
                role="listbox"
              >
                {countries.map((code) => (
                  <li
                    key={code}
                    data-code={code}
                    role="option"
                    aria-selected={code === selectedCountry}
                    className={`ea-nip-input__dropdown-item${code === selectedCountry ? " selected" : ""}`}
                    onMouseDown={() => handleCountrySelect(code)}
                  >
                    <FlagImage code={code} />
                    <span>{code}</span>
                  </li>
                ))}
              </ul>
            )}
            <div className="ea-nip-input__divider" />
          </div>
        )}

        <Controller
          control={control}
          name={name}
          rules={validationRules}
          render={({ field }) => {
            const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
              const inputValue = e.target.value || "";
              const cleaned = inputValue.replace(/[^0-9A-Za-z]/g, "");

              if (!showCountrySelect || selectedCountryRef.current === "PL") {
                // Polska - zawsze 10 cyfr, tniemy nadmiar
                const limited = cleaned.replace(/\D/g, "").slice(0, 10);
                field.onChange(limited);
              } else {
                // Inne kraje - maks 14 znaków alfanumerycznych, tniemy nadmiar
                const limited = cleaned.slice(0, MAX_FOREIGN_NIP_LENGTH);
                field.onChange(limited);
              }
            };

            return (
              <input
                {...field}
                type="text"
                className="ea-nip-input__input"
                placeholder={!showCountrySelect || selectedCountry === "PL" ? "0000000000" : ""}
                aria-label={label}
                onChange={handleChange}
              />
            );
          }}
        />
      </div>
      {fieldError && (
        <p className="ea-nip-input__helper-text error">{fieldError.message}</p>
      )}
    </div>
  );
};

export default EANipInput;
